import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, LogOut, RefreshCw, ShieldCheck } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { API_BASE } from "@/lib/apiBase";

const API = API_BASE;
const STATUSES = ["new", "reviewing", "contacted", "approved", "declined"];

const STATUS_STYLES = {
  new: "border-aqua/50 bg-aqua/10 text-aqua",
  reviewing: "border-yellow-500/40 bg-yellow-400/10 text-yellow-700",
  contacted: "border-sky-500/40 bg-sky-400/10 text-sky-700",
  approved: "border-emerald-500/40 bg-emerald-400/10 text-emerald-700",
  declined: "border-red-500/40 bg-red-400/10 text-red-600",
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
};

const DETAIL_ROWS = [
  ["Contact", (a) => `${a.first_name} ${a.last_name} · ${a.job_title}`],
  ["Email / Phone", (a) => `${a.email} · ${a.phone}`],
  ["Business", (a) => `${a.legal_business_name}${a.dba ? ` (DBA: ${a.dba})` : ""}`],
  ["Website", (a) => a.website],
  ["Business Address", (a) => `${a.business_address}, ${a.city}, ${a.state} ${a.zip_code}`],
  ["Years / Type", (a) => `${a.years_in_business} · ${a.business_type}`],
  ["Waterfront Location", (a) => a.waterfront_address],
  ["Water Access", (a) => `${a.water_access_type} · ${a.dock_launch_access}`],
  ["Current Products", (a) => a.current_rental_products],
  ["Units / Months", (a) => `${a.rental_units_count} units · ${a.months_of_operation}`],
  ["Weekly Volume / Staff", (a) => `${a.weekly_customer_volume} · ${a.staff_count} staff`],
  ["Booking Platform", (a) => a.booking_platform],
  ["Insurance / Storage", (a) => `${a.commercial_insurance} · ${a.available_storage}`],
  ["Staff Availability", (a) => a.staff_availability],
  ["Deployment Timeline", (a) => a.deployment_timeline],
  ["Add-on Interest", (a) => a.marketing_addons_interest],
  ["Additional Info", (a) => a.additional_info],
];

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/applications`, { withCredentials: true });
      setApps(data);
    } catch {
      toast.error("Could not load applications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/applications/${id}`, { status }, { withCredentials: true });
      setApps((prev) => prev.map((a) => (a.application_id === id ? { ...a, status } : a)));
      toast.success(`Marked ${id} as ${status}.`);
    } catch {
      toast.error("Status update failed.");
    }
  };

  const signOut = async () => {
    await logout();
    navigate("/");
  };

  const visible = filter === "all" ? apps : apps.filter((a) => (a.status || "new") === filter);

  return (
    <div data-testid="admin-page">
      <Navbar />
      <main className="min-h-screen bg-ink pb-24 pt-32">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="eyebrow mb-2 flex items-center gap-2"><ShieldCheck size={14} /> Admin · Fleet Partner Leads</p>
              <h1 className="font-display text-3xl font-extrabold tracking-tight text-offwhite" data-testid="admin-title">
                Applications
              </h1>
              <p className="mt-2 text-sm text-muted">
                Signed in as {user?.name || user?.email} · {apps.length} total
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={load} className="btn-ghost !px-4 !py-2 !text-xs" data-testid="admin-refresh-btn">
                <RefreshCw size={13} /> Refresh
              </button>
              <button onClick={signOut} className="btn-ghost !px-4 !py-2 !text-xs" data-testid="admin-signout-btn">
                <LogOut size={13} /> Sign out
              </button>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-filters">
            {["all", ...STATUSES].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`admin-filter-${s}`}
                className={`rounded-full border px-4 py-1.5 text-xs font-semibold capitalize transition-colors ${
                  filter === s ? "border-aqua bg-aqua/15 text-aqua" : "border-black/10 text-muted hover:border-aqua/40"
                }`}
              >
                {s === "all" ? `All (${apps.length})` : `${s} (${apps.filter((a) => (a.status || "new") === s).length})`}
              </button>
            ))}
          </div>

          <div className="mt-6 space-y-3">
            {loading ? (
              <p className="py-16 text-center text-sm text-muted">Loading applications…</p>
            ) : visible.length === 0 ? (
              <p className="card-line rounded-xl py-16 text-center text-sm text-muted" data-testid="admin-empty">
                No applications in this view yet.
              </p>
            ) : (
              visible.map((a) => {
                const status = a.status || "new";
                const expanded = open === a.application_id;
                return (
                  <div key={a.application_id} className="card-line overflow-hidden rounded-xl" data-testid={`admin-row-${a.application_id}`}>
                    <button
                      className="flex w-full flex-wrap items-center gap-x-5 gap-y-2 px-5 py-4 text-left sm:px-6"
                      onClick={() => setOpen(expanded ? null : a.application_id)}
                      data-testid={`admin-expand-${a.application_id}`}
                    >
                      <span className="font-mono text-xs text-aqua">{a.application_id}</span>
                      <span className="min-w-40 flex-1 truncate text-sm font-semibold text-offwhite">{a.legal_business_name}</span>
                      <span className="hidden text-xs text-muted md:block">{a.first_name} {a.last_name}</span>
                      <span className="hidden text-xs text-muted lg:block">{a.city}, {a.state}</span>
                      <span className="text-xs text-muted">{fmtDate(a.created_at)}</span>
                      <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${STATUS_STYLES[status]}`}>
                        {status}
                      </span>
                      <ChevronDown size={15} className={`text-muted transition-transform duration-300 ${expanded ? "rotate-180" : ""}`} />
                    </button>
                    {expanded && (
                      <div className="border-t border-black/[0.07] px-5 py-6 sm:px-6" data-testid={`admin-detail-${a.application_id}`}>
                        <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
                          {DETAIL_ROWS.map(([label, get]) => (
                            <div key={label}>
                              <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted/70">{label}</dt>
                              <dd className="mt-1 text-sm text-offwhite/90">{get(a) || "—"}</dd>
                            </div>
                          ))}
                        </dl>
                        <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-black/[0.07] pt-5">
                          <span className="mr-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted/70">Set status</span>
                          {STATUSES.map((s) => (
                            <button
                              key={s}
                              onClick={() => setStatus(a.application_id, s)}
                              data-testid={`admin-status-${a.application_id}-${s}`}
                              className={`rounded-full border px-3 py-1 text-[11px] font-semibold capitalize transition-colors ${
                                status === s ? STATUS_STYLES[s] : "border-black/10 text-muted hover:border-aqua/40 hover:text-offwhite"
                              }`}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <p className="mt-8 text-xs text-muted/70">
            Need the public site? <Link to="/" className="text-aqua underline underline-offset-2">Return to homepage</Link>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
